import {
  useCreateVersionVulnerabilityAnnotationMutation,
  useUpdateVersionVulnerabilityAnnotationMutation,
} from '../applications.api';

/**
 * Convenience wrapper that picks create vs. update based on the presence of
 * `annotationId` in the payload. Mirrors
 * `useUpsertVulnerabilityAnnotationMutation` from the artifact feature.
 */
export const useUpsertVersionVulnerabilityAnnotationMutation = () => {
  const [
    create,
    { error: createError, isLoading: isCreating, reset: resetCreate },
  ] = useCreateVersionVulnerabilityAnnotationMutation();
  const [
    update,
    { error: updateError, isLoading: isUpdating, reset: resetUpdate },
  ] = useUpdateVersionVulnerabilityAnnotationMutation();

  const error = createError ?? updateError;
  const isLoading = isCreating || isUpdating;

  type CreateArgs = Parameters<typeof create>[0];
  type UpdateArgs = Parameters<typeof update>[0];

  // Update needs only the routing ids plus the (dirty) annotation fields;
  // create additionally needs the package reference. Accept either shape.
  const upsert = async (
    payload: { annotationId?: string } & (CreateArgs | UpdateArgs),
  ) =>
    payload.annotationId
      ? update(payload as UpdateArgs)
      : create(payload as CreateArgs);

  const reset = () => {
    resetCreate();
    resetUpdate();
  };

  return [
    upsert,
    {
      createError,
      error,
      isCreating,
      isLoading,
      isUpdating,
      reset,
      updateError,
    },
  ] as const;
};
